import { type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Info, CheckCircle2, TriangleAlert, XCircle } from "lucide-react";
import { type LucideProps } from "lucide-react";
import { cn } from "@/lib/cn";

const alertVariants = cva(
  "flex gap-3 rounded-xl border p-4 text-sm",
  {
    variants: {
      tone: {
        info: "border-info/30 bg-info/10 text-fg-primary",
        success: "border-success/30 bg-success/10 text-fg-primary",
        warning: "border-warning/30 bg-warning/10 text-fg-primary",
        danger: "border-danger/30 bg-danger/10 text-fg-primary",
      },
    },
    defaultVariants: { tone: "info" },
  },
);

const iconTone = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
} as const;

const defaultIcons: Record<NonNullable<AlertTone>, (props: LucideProps) => ReactNode> = {
  info: (p) => <Info {...p} />,
  success: (p) => <CheckCircle2 {...p} />,
  warning: (p) => <TriangleAlert {...p} />,
  danger: (p) => <XCircle {...p} />,
};

type AlertTone = VariantProps<typeof alertVariants>["tone"];

export interface AlertProps extends VariantProps<typeof alertVariants> {
  title?: ReactNode;
  children?: ReactNode;
  /** Optional trailing action (button/link), rendered top-right. */
  action?: ReactNode;
  /** Set false to hide the leading icon. */
  icon?: boolean;
  className?: string;
}

/**
 * Alert / Callout — inline message banner (DESIGN-SYSTEM §5).
 * tone: info | success | warning | danger; icon + title + body + optional action.
 */
export function Alert({
  tone = "info",
  title,
  children,
  action,
  icon = true,
  className,
}: AlertProps) {
  const t = tone ?? "info";
  const renderIcon = defaultIcons[t];
  return (
    <div role="alert" className={cn(alertVariants({ tone }), className)}>
      {icon && (
        <span className={cn("mt-0.5 shrink-0", iconTone[t])}>
          {renderIcon({ size: 18, strokeWidth: 2, "aria-hidden": true })}
        </span>
      )}
      {/* The action drops under the text when both don't fit side by side;
          next to it on a phone, the text was squeezed to a word per line. */}
      <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-[12rem] flex-1 space-y-1">
          {title && <p className="font-medium leading-snug text-fg-primary">{title}</p>}
          {children && <div className="text-fg-secondary">{children}</div>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}
Alert.displayName = "Alert";
